import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { ButtonSizeEnum } from '../../shared/components/button/types/button-size.enum';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { InputComponent } from '../../shared/components/input/input.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { TagTableComponent } from '../../shared/components/tag-table/tag-table.component';
import { Sorts } from '../../shared/services/pagination/models/typies/sorts.type';
import { ModalAction } from '../../shared/modals/modal-actions/modal-action.interface';
import { PaginationDto } from '../../shared/services/pagination/models/interfaces/pagination-dto';
import { CrudRequestParams } from '../../shared/services/pagination/models/interfaces/crud';
import { ModalService } from '../../shared/services/modal/modal.service';
import { createCancelConfirmButtons, openConfirmModal, openSuccessModal } from '../../helpers/modal.helper';
import { Tag } from '../../shared/models/interfaces/tag.interface';
import { normalizeTagName, TagService } from '../../shared/services/tag/tag.service';
import { CreateTagModalComponent, CreateTagModalData } from './modals/create-tag-modal.component';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../shared/utils/functions';
import { PermissionService } from '../../shared/services/permission/permission.service';
import { HasPermission } from '../../shared/directives/has-permission/has-permission.directive';
import { PermissionEnum, ResourceEnum } from '../../../environments/shared-environment/permission';

@Component({
  selector: 'l-tag',
  standalone: true,
  imports: [
    IconComponent,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    TagTableComponent,
    HasPermission,
  ],
  templateUrl: './tag.component.html',
})
export class TagComponent implements OnInit, OnDestroy {
  constructor(
    private readonly tagService: TagService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly permissionService: PermissionService,
  ) {}

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly resourceEnum = ResourceEnum;

  public readonly permissionEnum = PermissionEnum;

  public readonly searchControl = new FormControl('');

  public isLoading = false;

  public limit = 10;

  public page = 1;

  public sort: Sorts = { field: 'updatedAt', order: 'DESC' };

  public source: PaginationDto<Tag> = {
    data: [],
    page: 1,
    pageCount: 0,
    count: 0,
    total: 0,
  };

  private readonly destroy$$ = new Subject<void>();

  private readonly modalWidth = '700px';

  public get tags(): Tag[] {
    return this.source.data;
  }

  public get total(): number {
    return this.source.total;
  }

  public async ngOnInit(): Promise<void> {
    this.searchControl.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$$),
    ).subscribe((value) => {
      void this.loadTags(1, value || '');
    });

    await this.loadTags(1);
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public openCreateTagModal(): void {
    this.openTagModal({}, 'create');
  }

  public async onChangePage(page: number): Promise<void> {
    await this.loadTags(page);
  }

  public async onChangeSort(sort?: Sorts): Promise<void> {
    this.sort = sort || { field: 'updatedAt', order: 'DESC' };
    await this.loadTags(1);
  }

  public getRowActions(tag: Tag): ModalAction[] {
    const actions: ModalAction[] = [];

    if (this.permissionService.has(ResourceEnum.TAGS, PermissionEnum.UPDATE)) {
      actions.push({
        label: 'Editar',
        icon: 'edit',
        callback: () => this.openTagModal({ tag }, 'edit'),
      });
    }

    if (this.permissionService.has(ResourceEnum.TAGS, PermissionEnum.REMOVE)) {
      actions.push({
        label: 'Excluir',
        icon: 'delete',
        colorClass: 'text-lDanger',
        callback: () => this.openTagDeleteModal(tag),
      });
    }

    return actions;
  }

  private async loadTags(page: number, search?: string): Promise<void> {
    try {
      this.isLoading = true;

      const searchValue = normalizeTagName(search ?? this.searchControl.value ?? '');

      // Excluir uma tag na API apenas a desativa, por isso a listagem mostra somente as ativas
      const params: CrudRequestParams<Tag> = {
        page,
        limit: this.limit,
        sort: [{ field: this.sort.field, order: this.sort.order }],
        search: searchValue
          ? { isActive: { $eq: true }, name: { $contL: searchValue } }
          : { isActive: { $eq: true } },
      };

      this.source = await this.tagService.getMany(params);
      this.page = this.source.page;
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private openTagModal(data: CreateTagModalData, mode: 'create' | 'edit'): void {
    const ref = this.modalService.open(CreateTagModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data,
    });

    ref.onClose.pipe(takeUntil(this.destroy$$)).subscribe((result?: Tag) => {
      if (!result) {
        return;
      }

      void this.handleTagSaved(mode);
    });
  }

  private openTagDeleteModal(tag: Tag): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Excluir tag',
        description: `Tem certeza que deseja excluir a tag "${tag.name}"? Tags associadas a cursos não podem ser excluídas.`,
        buttons: createCancelConfirmButtons('Excluir', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.deleteTag(tag);
      },
      this.destroy$$,
    );
  }

  private async deleteTag(tag: Tag): Promise<void> {
    try {
      await this.tagService.delete(tag.id);
      await this.loadTags(this.tags.length === 1 && this.page > 1 ? this.page - 1 : this.page);
      this.toastService.success('Tag excluída com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Não foi possível excluir a tag');
    }
  }

  private async handleTagSaved(mode: 'create' | 'edit'): Promise<void> {
    await this.loadTags(mode === 'create' ? 1 : this.page);

    openSuccessModal(
      this.modalService,
      this.modalWidth,
      {
        title: mode === 'create' ? 'Tag criada com sucesso!' : 'Tag editada com sucesso!',
        subtitle: 'Você pode visualizá-la através da lista de tags',
        imageAlt: mode === 'create' ? 'Tag criada com sucesso' : 'Tag editada com sucesso',
      },
      undefined,
      this.destroy$$,
    );
  }
}
